import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import { Box, Button, Checkbox, FormControlLabel, Stack, Typography } from '@mui/material';
import useDevicesTab from 'hooks/allVisitors/pages/useVisitorsGroups/pages/useDevicesTab';
import useTranslation from 'hooks/useTranslation/useTranslation';
import * as React from 'react';
import { Controller, useForm } from 'react-hook-form';
import Loader from 'v2/components/Loader/Loader';
import useSnackbarContext from 'v2/components/Snackbar/useSnackbarContext';
import useWizardStepContext from 'v2/components/WizardModal/useWizardStepContext';

const FORM_ID = 'create-group-step-4';
const STEP_INDEX = 3;

interface FormValues {
  deviceGroupIds: Record<string, boolean>;
}

interface Props {
  groupId: string | null;
  accessGrantId: string | null;
}

const GroupDevicesStep: React.FC<Props> = ({ groupId, accessGrantId }) => {
  const { translation } = useTranslation();
  const { showSnackbar } = useSnackbarContext();
  const { goToPrev, goToNext, setFooterContent } = useWizardStepContext();

  const { deviceGroups, deviceGroupsLoading, assignToAccessGrants, assignToAccessGrantsMutationLoading } =
    useDevicesTab({
      handleFetchError: (msg) => showSnackbar(msg, { severity: 'error' }),
      visitorGroupId: groupId ?? ''
    });

  const { handleSubmit, control } = useForm<FormValues>({
    mode: 'onTouched',
    defaultValues: { deviceGroupIds: {} }
  });

  const onSubmit = handleSubmit(async (data) => {
    try {
      if (accessGrantId) {
        const selectedIds = Object.entries(data.deviceGroupIds)
          .filter(([, checked]) => checked)
          .map(([id]) => id);
        if (selectedIds.length > 0) {
          await assignToAccessGrants([accessGrantId], [], selectedIds);
        }
      }
      goToNext();
    } catch {
      // Error displayed via snackbar in useDevicesTab
    }
  });

  React.useLayoutEffect(() => {
    setFooterContent(
      STEP_INDEX,
      <Box sx={{ display: 'flex', gap: 2 }}>
        <Button
          key="group-devices-step-back"
          id="group-devices-back-button"
          variant="outlined"
          onClick={goToPrev}
          type="button"
        >
          {translation.back}
        </Button>
        <Button
          key="group-devices-step-submit"
          id="group-devices-finish-button"
          variant="contained"
          type="submit"
          form={FORM_ID}
          loading={assignToAccessGrantsMutationLoading}
          endIcon={<ChevronRightIcon />}
        >
          {translation.finish}
        </Button>
      </Box>
    );
  }, [assignToAccessGrantsMutationLoading, setFooterContent, translation.finish]);

  if (deviceGroupsLoading) {
    return <Loader />;
  }

  return (
    <Stack component="form" id={FORM_ID} onSubmit={onSubmit} noValidate gap={1}>
      {deviceGroups.length === 0 ? (
        <Typography>{translation.no_devices_configure_later}</Typography>
      ) : (
        deviceGroups.map((group) => (
          <Controller
            key={group.id}
            name={`deviceGroupIds.${group.id}`}
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Checkbox
                    id={`device-group-${group.id}-checkbox`}
                    checked={!!field.value}
                    onChange={field.onChange}
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FolderOutlinedIcon fontSize="small" />
                    {group.name}
                  </Box>
                }
              />
            )}
          />
        ))
      )}
    </Stack>
  );
};

export default GroupDevicesStep;
